import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import type { TCurrentTime } from 'libs/design-system/src/types/time_picker';
import { TimePicker, ToggleBox } from '@unified-checkout/design-system';

import type { TAppliedRanges } from '@app/dashboard/types/innerLayout/date-ranges';
import type { IFields } from '@app/dashboard/types/innerLayout/payment_links/single_quick_link';
import {
  DATE_PLACE_HOLDER,
  EXPIRATION_DATE_INITIAL_VALUES,
  SINGLE_PAYMENTS_FIELDS,
} from '@app/dashboard/constants/payment-links/single-payments';
import {
  SINGLE_PAYMENT_LINKS_EVENTS,
  SINGLE_PAYMENT_LINKS_EVENTS_PARAMS,
} from '@app/dashboard/constants/tracking_events/payment_links/single';
import DateRanges from '@app/dashboard/components/inner_layout/DateRanges';
import { onFieldChangeHandler } from '@app/dashboard/utils/formValidations';
import { useLangStore } from '@app/dashboard/store/lang';
import { customLocales } from '@app/dashboard/utils/dateRangePicker';
import {
  formatDateAndTime,
  paymentLinksDatePresets,
} from '../../../../../utils';

import styles from './expiration-date.module.scss';

const ExpirationDate = ({ fields, setFields }: IFields) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const [shouldHaveExpDate, setShouldHaveExpDate] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  const date = fields[SINGLE_PAYMENTS_FIELDS.DATE];
  const time = fields[SINGLE_PAYMENTS_FIELDS.TIME];

  const preDefinedRanges = useMemo(() => paymentLinksDatePresets(t), [t]);

  const defaultPreDefinedRange = useMemo(
    () => preDefinedRanges.find((range) => range.default),
    [preDefinedRanges]
  );

  const handleFieldChange = (value: any, field: string) => {
    onFieldChangeHandler(fields, setFields, field, value, () => {});
  };

  const onApplyDateHandler = (range: TAppliedRanges) => {
    handleFieldChange(range, SINGLE_PAYMENTS_FIELDS.DATE);
  };

  const onApplyTimeHandler = (value: TCurrentTime | undefined) => {
    handleFieldChange(value, SINGLE_PAYMENTS_FIELDS.TIME);
  };

  const onClearDateHandler = () => {
    handleFieldChange('', SINGLE_PAYMENTS_FIELDS.DATE);
  };

  const handleToggleChange = (checked: boolean) => {
    if (!isTouched) return;
    amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.RESTRICTIONS_UPDATED, {
      [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.NO_EXP_DATE]: checked,
    });
    setShouldHaveExpDate(checked);
  };

  useEffect(() => {
    if (shouldHaveExpDate) {
      setFields((prev: any) => {
        return {
          ...prev,
          ...EXPIRATION_DATE_INITIAL_VALUES,
        };
      });
    } else {
      setFields((prev: any) => {
        const updatedFields = { ...prev };
        Object.keys(EXPIRATION_DATE_INITIAL_VALUES).forEach((key) => {
          delete updatedFields[key];
        });
        return updatedFields;
      });
    }
  }, [shouldHaveExpDate]);

  useEffect(() => {
    if (date?.value && time?.value) {
      const dateValue = date.value as unknown as { start: Date };
      const timeValue = time.value as TCurrentTime;

      const expirationDate = formatDateAndTime(dateValue.start, timeValue);

      amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.RESTRICTIONS_UPDATED, {
        [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.EXP_DATE_TIME]: expirationDate,
      });
    }
  }, [date, time]);

  return (
    <div className={styles['expiration-date']}>
      <ToggleBox
        size="sm"
        type="transparent box"
        text={t(
          'innerLayout.paymentLinksCreationPages.sections.restrictions.hasExpDate'
        )}
        checked={shouldHaveExpDate}
        onToggleChangeHandler={handleToggleChange}
        onBeforeToggleConfirmation={async () => {
          if (!isTouched) {
            setIsTouched(true);
          }
          return true;
        }}
      />

      {shouldHaveExpDate && !!fields[SINGLE_PAYMENTS_FIELDS.DATE] && (
        <div
          className={styles['expiration-date__pickers']}
          data-date-has-error={!!date?.errorMsg}
        >
          <div className={styles['expiration-date__pickers-box']}>
            <DateRanges
              appliedRanges={date?.value as TAppliedRanges}
              handleRangesChange={onApplyDateHandler}
              lang={lang}
              minDate={defaultPreDefinedRange?.selectionRange[0]}
              maxDate={defaultPreDefinedRange?.selectionRange[1]}
              onClear={onClearDateHandler}
              customLocale={customLocales}
              customPreDefinedRanges={preDefinedRanges}
              shouldSelectOneDate
              verticalPlacement="auto"
              horizontalPlacement="start"
              placeholder={
                lang === 'en' ? DATE_PLACE_HOLDER.EN : DATE_PLACE_HOLDER.AR
              }
              shouldApplyDefaultRange={false}
              shouldHideBtnRangeName={true}
            />

            {!!date?.errorMsg && (
              <span className={styles['expiration-date__pickers-box-error']}>
                {date?.errorMsg}
              </span>
            )}
          </div>

          <TimePicker
            name={SINGLE_PAYMENTS_FIELDS.TIME}
            onChangeHandler={onApplyTimeHandler}
            defaultValue={(time?.value as TCurrentTime) || ''}
            horizontalPlacement="center"
            placeholder={t(
              'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.restrictions.selectTime'
            )}
            disabled={!date?.value}
          />
        </div>
      )}
    </div>
  );
};

export default ExpirationDate;
